import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { NotificationSettings } from './notification-settings';
import { OutlookConnectSettings } from './outlook-connect-settings';
import { SettingsPanelContext } from './settings-panel-context';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';

/**
 * Dedicated settings panel component for chat area.
 * Displays settings content inline within the main chat content area
 * instead of as a modal dialog overlay.
 *
 * Provides SettingsPanelContext so any settings card can push a full-panel
 * sub-view (e.g. the Outlook manage view) without this component needing to
 * know what's inside it.
 */
export function ChatSettingsPanel() {
  const [activeView, setActiveView] = useState<ReactNode>(null);
  const backHandlerRef = useRef<(() => void) | null>(null);
  const { settingsBackSignal, setIsSettingsOpen, setHasActiveSettingsSubView, settingsCloseIntentRef } =
    useChatOverlayContext();
  const initialSignalRef = useRef(settingsBackSignal);

  // When settings button is clicked while settings is open:
  // - If a sub-view is active and has a back handler, trigger it (dirty check)
  // - If a sub-view is active with no handler, pop it
  // - If no sub-view, close settings entirely
  useEffect(() => {
    if (settingsBackSignal === initialSignalRef.current) return;
    initialSignalRef.current = settingsBackSignal;

    if (activeView) {
      if (backHandlerRef.current) {
        backHandlerRef.current();
      } else {
        setActiveView(null);
      }
    } else {
      setIsSettingsOpen(false);
    }
  }, [settingsBackSignal, activeView, setIsSettingsOpen]);

  // Sync sub-view state and clear back handler when sub-view is removed.
  // If the sub-view was dismissed due to a "close settings" intent, close settings entirely.
  useEffect(() => {
    setHasActiveSettingsSubView(!!activeView);
    if (!activeView) {
      backHandlerRef.current = null;
      if (settingsCloseIntentRef.current) {
        settingsCloseIntentRef.current = false;
        setIsSettingsOpen(false);
      }
    }
  }, [activeView, setHasActiveSettingsSubView, settingsCloseIntentRef, setIsSettingsOpen]);

  return (
    <SettingsPanelContext.Provider
      value={useMemo(() => ({ pushView: setActiveView, popView: () => setActiveView(null), backHandlerRef }), [])}
    >
      <div className="flex size-full min-h-0 min-w-0 flex-col">
        {activeView ? (
          <div className="flex min-h-0 flex-1 flex-col">{activeView}</div>
        ) : (
          <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto p-6">
            <div className="flex w-full max-w-md flex-col gap-4">
              <NotificationSettings />
              <OutlookConnectSettings />
            </div>
          </div>
        )}
      </div>
    </SettingsPanelContext.Provider>
  );
}
